import { FlatFeeType } from '@ee/common/enums';
import { DocumentGenerationEntry, mapDocumentGenerationToDocuments } from '@ee/common/models';
import { SerializeCustomFieldValuesForWire } from './custom-field.form';

/**
 * Fee types/categories whose amount the SERVER resolves from the org's county fee settings on step
 * completion (mirrors BillableItemService's serverCalculatedFeeTypes / serverCalculatedCategoryIds).
 * A $0.00 shown in the UI for one of these does not mean $0.00 will be persisted, so they must be
 * submitted regardless of the displayed amount — the backend decides whether there's anything to bill.
 */
const SERVER_CALCULATED_FEE_TYPES: readonly FlatFeeType[] = [
  FlatFeeType.COUNTY_FEE, FlatFeeType.WRIT_FEE, FlatFeeType.SHERIFF_FEE, FlatFeeType.HEARING_FEE,
  FlatFeeType.CLIENT_ATTORNEY_FEE
];

const SERVER_CALCULATED_CATEGORY_IDS: readonly string[] = [
  'builtin-county-fee', 'builtin-writ-filing-fee', 'builtin-sheriff-fee', 'builtin-hearing-fee',
  'builtin-attorney-fee'
];

export function isServerCalculatedFee(item: { flat_fee_type?: FlatFeeType; category_id?: string } | null): boolean {
  return !!item && (
    SERVER_CALCULATED_FEE_TYPES.includes(item.flat_fee_type) ||
    SERVER_CALCULATED_CATEGORY_IDS.includes(item.category_id)
  );
}

/**
 * Normalizes a raw step-completion form value into the payload the backend binds.
 *
 * **Every submit boundary must go through this — there are three, not one.** Besides the
 * action-center `CompleteWorkflowStepComponent.save()`, the component grafts its own form onto a
 * host with `parentForm.setControl('step_completion_details', this.form)`, and two hosts read that
 * raw value straight into their own request without ever calling `save()`: the New-Eviction create
 * path and Inbox -> Process. A normalization applied only inside `save()` is therefore silently
 * absent from two of the three. That is exactly how MULTI_SELECT shipped broken, so the conversions
 * live here rather than in any one caller.
 *
 * - **MULTI_SELECT** controls hold a real array while the boxes are being ticked, but
 *   `CustomFieldDTO.value` is a `String` on the wire. Sending the array does not degrade
 *   gracefully — Jackson has no `UNWRAP_SINGLE_VALUE_ARRAYS` configured, so binding fails and the
 *   entire request is rejected. Joining here is what the server splits back apart with
 *   `CustomFieldUtils.splitMultiValueCell`. Nothing ticked sends `''` rather than null, because the
 *   server skips null-valued fields and that would lose a deliberate "none of these".
 * - **Zero-amount client-authored billables** are dropped; nothing bills $0, and they were being
 *   written to the case as real $0.00 Flat Fee items. Server-calculated fees are exempt and always
 *   sent: the backend resolves county / writ / sheriff / hearing / attorney fees from the org's own
 *   county fee settings, so a $0.00 displayed here does not mean $0.00 will be persisted.
 *
 * Returns a **new** object. Never mutate the form value in place — the controls must keep holding
 * their arrays so the checkbox groups stay bound to them.
 */
export function ToStepCompletionPayload<T extends { custom_fields?: any[]; billable_items?: any[] }>(value: T): T {
  return {
    ...value,
    custom_fields: SerializeCustomFieldValuesForWire(value?.custom_fields),
    billable_items: (value?.billable_items ?? []).filter(item =>
      Number(item?.amount) > 0 || isServerCalculatedFee(item))
  };
}

/**
 * {@link ToStepCompletionPayload} plus the `documents` mapping, for the paths that create a case.
 *
 * The `documents` mapping is specific to create: the action-center path lets the server derive it in
 * `EvictionService.saveStepCompletion`. Without it the selected templates are dropped and no documents
 * generate on create. Shared by both intake wizards (v1 `CreateEvictionComponent`, v2
 * `EvictionWizardV2Component`), which read the grafted step form's raw value rather than calling
 * `CompleteWorkflowStepComponent.save()`. A null/absent value passes through unchanged.
 */
export function ToCreateStepCompletionPayload<
  T extends { custom_fields?: any[]; billable_items?: any[]; document_generation?: DocumentGenerationEntry[] }
>(value: T | null | undefined): T | null | undefined {
  return value
    ? { ...ToStepCompletionPayload(value), documents: mapDocumentGenerationToDocuments(value.document_generation) }
    : value;
}
